/**
 * Scorecard Visibility Query Hook
 * TanStack Query hook for fetching scorecards with visibility data
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SCORECARD_VISIBILITY_API } from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/api';
import {
  type ScorecardItem,
  type GroupedScorecards,
} from '@/components/shared/interviews-revamp/scorecard/scorecard-visibility/types';
import { useShowToast } from '@/hooks';

// Query keys
export const SCORECARD_VISIBILITY_QUERY_KEYS = {
  scorecards: (applicationId: number, interviewId: number) => [
    'scorecard-visibility',
    'scorecards',
    applicationId,
    interviewId,
  ],
  base: ['scorecard-visibility'],
};

interface UseScorecardVisibilityQueryParams {
  applicationId: number;
  interviewId: number;
  enabled?: boolean;
  shouldShowHistory?: boolean | undefined;
  hasTransferInfo?: boolean;
}

/**
 * Hook to fetch scorecards with visibility data
 */
export const useScorecardVisibilityQuery = ({
  applicationId,
  interviewId,
  enabled = true,
  shouldShowHistory = false,
  hasTransferInfo = false,
}: UseScorecardVisibilityQueryParams) => {
  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- pre-existing; behavior preserved (extraction move)
  return useQuery({
    queryKey: SCORECARD_VISIBILITY_QUERY_KEYS.scorecards(
      applicationId,
      interviewId
    ),
    queryFn: () =>
      SCORECARD_VISIBILITY_API.getScorecardsByInterviewId({
        applicationId,
        interviewId,
        shouldShowHistory: shouldShowHistory,
        hasTransferInfo,
      }),
    enabled: enabled && !!applicationId && !!interviewId,
    staleTime: 0,
    select: (response: unknown) => {
      const envelope = response as {
        data?: {
          scorecards?: ScorecardItem[];
          transfer_info?: GroupedScorecards['transferInfo'];
        };
        scorecards?: ScorecardItem[];
        transfer_info?: GroupedScorecards['transferInfo'];
      };
      const raw = envelope?.data ?? envelope;
      const scorecards =
        raw?.scorecards ?? (Array.isArray(raw) ? raw : []) ?? [];
      const transferInfo = raw?.transfer_info ?? null;
      const grouped = groupScorecards(scorecards as ScorecardItem[]);
      return { ...grouped, transferInfo };
    },
  });
};

/**
 * Group scorecards into current interview and history
 */
const groupScorecards = (data: ScorecardItem[]) => {
  const currentInterview = data.filter((sc) => sc.is_current);
  const history = data.filter((sc) => !sc.is_current);
  return { currentInterview, history };
};

/**
 * Hook to send reminder for a scorecard
 */
export const useSendReminderMutation = () => {
  const queryClient = useQueryClient();
  const toast = useShowToast();

  return useMutation({
    mutationFn: SCORECARD_VISIBILITY_API.sendScorecardReminder,
    onSuccess: () => {
      toast({ title: 'Scorecard reminder sent successfully' });
      // Invalidate related queries
      queryClient.invalidateQueries({
        queryKey: SCORECARD_VISIBILITY_QUERY_KEYS.base,
      });
    },
    onError: () => {
      toast({
        title: 'Failed to send scorecard reminder',
        status: 'error',
      });
    },
  });
};

/**
 * Hook to submit a scorecard
 */
export const useSubmitScorecardMutation = () => {
  const queryClient = useQueryClient();
  const toast = useShowToast();

  return useMutation({
    mutationFn: SCORECARD_VISIBILITY_API.submitScorecard,
    onSuccess: () => {
      toast({ title: 'Scorecard submitted successfully' });
      queryClient.invalidateQueries({
        queryKey: SCORECARD_VISIBILITY_QUERY_KEYS.base,
      });
    },
    onError: () => {
      toast({
        title: 'Scorecard submission failed',
        status: 'error',
      });
    },
  });
};
